import { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../utils/firebase';
import { StudyParticipant, ParticipantStatus } from '../types/study.types';
import { retryFirebaseCall } from '../utils/retry';

interface APIResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface UseParticipantsReturn {
  participants: StudyParticipant[];
  loading: boolean;
  error: string | null;
  withdrawParticipant: (participantId: string, reason?: string) => Promise<void>;
  markLostToFollowup: (participantId: string) => Promise<void>;
  resendSurveyEmail: (participantId: string, timepointId: string) => Promise<void>;
  getParticipant: (participantId: string) => Promise<StudyParticipant>;
  filterByStatus: (status: ParticipantStatus | 'all') => StudyParticipant[];
}

export const useParticipants = (studyId: string): UseParticipantsReturn => {
  const [participants, setParticipants] = useState<StudyParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for participants
  useEffect(() => {
    if (!studyId) {
      setLoading(false);
      setError('Study ID is required');
      return;
    }

    const q = query(
      collection(db, 'studies', studyId, 'participants'),
      orderBy('enrolledAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const participantsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as StudyParticipant[];
        setParticipants(participantsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching participants:', err);
        setError(err.message || 'Failed to fetch participants');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [studyId]);

  /**
   * Withdraw a participant from the study
   */
  const withdrawParticipant = useCallback(
    async (participantId: string, reason?: string): Promise<void> => {
      if (!studyId) {
        throw new Error('Study ID is required');
      }

      setError(null);

      try {
        const withdrawFn = httpsCallable<
          { studyId: string; participantId: string; reason?: string },
          APIResponse<void>
        >(functions, 'withdrawParticipant');

        const result = await retryFirebaseCall(
          () => withdrawFn({ studyId, participantId, reason }),
          'withdrawParticipant'
        );

        if (!result.data.success) {
          throw new Error(result.data.error || 'Failed to withdraw participant');
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to withdraw participant';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [studyId]
  );

  /**
   * Mark a participant as lost to follow-up
   */
  const markLostToFollowup = useCallback(
    async (participantId: string): Promise<void> => {
      if (!studyId) {
        throw new Error('Study ID is required');
      }

      setError(null);

      try {
        const markLostFn = httpsCallable<
          { studyId: string; participantId: string },
          APIResponse<void>
        >(functions, 'markLostToFollowup');

        const result = await retryFirebaseCall(
          () => markLostFn({ studyId, participantId }),
          'markLostToFollowup'
        );

        if (!result.data.success) {
          throw new Error(result.data.error || 'Failed to update participant status');
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to update participant status';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [studyId]
  );

  /**
   * Resend a survey email to a participant
   */
  const resendSurveyEmail = useCallback(
    async (participantId: string, timepointId: string): Promise<void> => {
      if (!studyId) {
        throw new Error('Study ID is required');
      }

      setError(null);

      try {
        const resendFn = httpsCallable<
          { studyId: string; participantId: string; timepointId: string },
          APIResponse<void>
        >(functions, 'resendSurveyEmail');

        const result = await retryFirebaseCall(
          () => resendFn({ studyId, participantId, timepointId }),
          'resendSurveyEmail'
        );

        if (!result.data.success) {
          throw new Error(result.data.error || 'Failed to resend survey email');
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to resend survey email';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [studyId]
  );

  /**
   * Get a single participant by ID
   */
  const getParticipant = useCallback(
    async (participantId: string): Promise<StudyParticipant> => {
      if (!studyId) {
        throw new Error('Study ID is required');
      }

      setError(null);

      try {
        const getParticipantFn = httpsCallable<
          { studyId: string; participantId: string },
          APIResponse<StudyParticipant>
        >(functions, 'getParticipant');

        const result = await retryFirebaseCall(
          () => getParticipantFn({ studyId, participantId }),
          'getParticipant'
        );

        if (!result.data.success || !result.data.data) {
          throw new Error(result.data.error || 'Failed to get participant');
        }

        return result.data.data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to get participant';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [studyId]
  );

  /**
   * Filter participants by status (client-side)
   */
  const filterByStatus = useCallback(
    (status: ParticipantStatus | 'all'): StudyParticipant[] => {
      if (status === 'all') {
        return participants;
      }
      return participants.filter((p) => p.status === status);
    },
    [participants]
  );

  return {
    participants,
    loading,
    error,
    withdrawParticipant,
    markLostToFollowup,
    resendSurveyEmail,
    getParticipant,
    filterByStatus,
  };
};
